import { onValue, orderByChild, query, remove, set, ref, equalTo } from 'firebase/database';
import {
  FETCH_DOCUMENT_USERS,
  FETCH_DOCUMENT_USERS_SUCCESS,
  FETCH_DOCUMENT_USERS_FAILED,
  UPDATE_DOCUMENT_USERS,
} from '../store/types';
import { firebase } from '../config/configureFirebase';
import { getDownloadURL, uploadBytes, ref as storageRef } from 'firebase/storage';

export const fetchDocumentUsers = () => (dispatch) => {
  try {
    const {
      documentUserRef
    } = firebase;

    if (!documentUserRef) {
      console.error("Firebase documentUserRef is not properly initialized");
      dispatch({
        type: FETCH_DOCUMENT_USERS_FAILED,
        payload: "Firebase reference not initialized"
      });
      return;
    }

    dispatch({
      type: FETCH_DOCUMENT_USERS,
      payload: null
    });

    onValue(
      query(documentUserRef, orderByChild('created_at')),
      (snapshot) => {
        const data = snapshot.val();
        console.log("📥 Fetched document users from Firebase:", {
          total_entries: data ? Object.keys(data).length : 0,
          sample_entries: data ? Object.keys(data).slice(0, 3) : []
        });
        
        if (data) {
          const sortedData = Object.entries(data)
            .sort(([, a], [, b]) => (b.created_at || 0) - (a.created_at || 0))
            .reduce((r, [k, v]) => ({ ...r, [k]: v }), {});

          console.log("📥 Processed document users:", {
            total_processed: Object.keys(sortedData).length,
            sample_data: Object.values(sortedData).slice(0, 2).map(d => ({
              id: d.id,
              user_id: d.user_id,
              field_id: d.field_id,
              status: d.status
            }))
          });

          dispatch({
            type: FETCH_DOCUMENT_USERS_SUCCESS,
            payload: sortedData
          });
        } else {
          console.log("📥 No document users data found in Firebase");
          dispatch({
            type: FETCH_DOCUMENT_USERS_FAILED,
            payload: "Unable to fetch document user settings."
          });
        }
      },
      (error) => {
        console.error("Firebase onValue error:", error);
        dispatch({
          type: FETCH_DOCUMENT_USERS_FAILED,
          payload: error.message
        });
      }
    );
  } catch (error) {
    console.error("fetchDocumentUsers error:", error);
    dispatch({
      type: FETCH_DOCUMENT_USERS_FAILED,
      payload: error.message
    });
  }
};

// Optimized: fetch only documents for a specific user to reduce payload and UI blocking
export const fetchDocumentUsersForUser = (userId) => (dispatch) => {
  try {
    const { documentUserRef } = firebase;
    if (!documentUserRef || !userId) {
      dispatch({ type: FETCH_DOCUMENT_USERS_FAILED, payload: "Missing reference or userId" });
      return;
    }

    dispatch({ type: FETCH_DOCUMENT_USERS, payload: null });

    const q = query(documentUserRef, orderByChild('user_id'), equalTo(userId));
    onValue(
      q,
      (snapshot) => {
        console.log('snapshot document users: ', snapshot.val());
        const data = snapshot.val() || {};
        // No heavy sort needed; keep as-is to minimize JS work
        dispatch({ type: FETCH_DOCUMENT_USERS_SUCCESS, payload: data });
      },
      (error) => {
        console.error("fetchDocumentUsersForUser onValue error:", error);
        dispatch({ type: FETCH_DOCUMENT_USERS_FAILED, payload: error.message });
      }
    );
  } catch (error) {
    console.error("fetchDocumentUsersForUser error:", error);
    dispatch({ type: FETCH_DOCUMENT_USERS_FAILED, payload: error.message });
  }
};

export const deleteDocumentUser = (id, onSuccess, onError) => (dispatch) => {
  try {
    const {
      documentUserEditRef
    } = firebase;

    if (!documentUserEditRef) {
      onError();
      return;
    }

    dispatch({
      type: UPDATE_DOCUMENT_USERS,
      payload: id
    });

    remove(documentUserEditRef(id));
    onSuccess();
  } catch (error) {
    onError();
  }
};

export const updateDocumentUsers = (
  data,
  onSuccess,
  onError
) => (dispatch) => {
  try {
    const {
      documentUserEditRef
    } = firebase;

    if (!documentUserEditRef) {
      onError();
      return;
    }

    dispatch({
      type: UPDATE_DOCUMENT_USERS,
      payload: data
    });

    console.log("new user data: ", data);
    console.log("📤 Saving document to Firebase:", {
      id: data.id,
      user_id: data.user_id,
      field_id: data.field_id,
      user_name: data.user_name,
      status: data.status,
      approved: data.approved,
      new_files_count: data.new_files?.length || 0,
      files_count: data.files?.length || 0
    });

    set(documentUserEditRef(data.id), data);
    console.log("✅ Document saved to Firebase successfully");
    onSuccess();
  } catch (error) {
    onError();
  }
};

export const uploadImageToStorage = async (imageFile, path, options = {}) => {
  const { storage } = firebase;
  const imageRef = storageRef(storage, path);
  const metadata = options.metadata || { contentType: imageFile.type || 'image/jpeg' };
  const uploadTask = await uploadBytes(imageRef, imageFile, metadata);
  const downloadURL = await getDownloadURL(imageRef);
  return downloadURL;
}; 